<template>
  <div class="shop-expiry">
    <div class="shop-expiry__row">
      <InputComponent :label="labels['signableFrom']" data-field="signableFrom">
        <template #default>
          <InputDate
            :value="formatDate(value.data.signableFrom)"
            @input="$ev => setDate('value.data.signableFrom', $ev)"
          />
        </template>
      </InputComponent>
      <InputComponent :label="labels['expiresAt']" data-field="expiresAt">
        <template #default>
          <InputDate :value="formatDate(value.expiresAt)" @input="$ev => setDate('value.expiresAt', $ev)" />
        </template>
      </InputComponent>
    </div>
    <div class="row between mt-1">
      <a v-if="!value.data.partials" href="#" @click.prevent="setPartials" class="shop-expiry__extend">
        <FontAwesomeIcon :icon="['fal', 'calendar-plus']" />
        {{ $t('setPartialDeadlines') }}
      </a>
      <a v-if="canRemovePartials" href="#" @click.prevent="unsetPartials" class="shop-expiry__extend">
        <FontAwesomeIcon :icon="['fal', 'calendar-times']" />
        {{ $t('removePartialDeadlines') }}
      </a>
      <DeliveryLog :value="getDeliveryLog(`shop-sign-info:${contract.id}`)" :resource="$t('info')" />
    </div>

    <DataTable
      v-if="value.data.partials"
      class="shop-expiry__partials"
      theme="border"
      :value="value.data.partials"
      :columns="[
        { id: 'label', label: $tc('partialDeadline') },
        { id: 'from', label: labels.signableFrom },
        { id: 'expires', label: labels.expiresAt },
      ]"
      :cell-wrapper="
        (item, col) => ({
          component: 'div',
          props: {
            class: {
              'shop-expiry__partial-category': col.id === 'label',
            },
          },
        })
      "
      :row-wrapper="
        item => ({
          component: 'div',
          props: {
            class: {
              'shop-expiry__partial': true,
              'shop-expiry__partial--disabled': ['signed', 'accepted'].includes(getPartialStatus(item.reference)),
            },
          },
        })
      "
    >
      <template #label="{ item }">
        {{ getCategoryLabel(item.reference) }}
      </template>
      <template #from="{ item, rowIndex }">
        <InputComponent data-field="signableFrom">
          <template #default>
            <InputDate
              :value="formatDate(item.signableFrom)"
              @input="$ev => setDate(`value.data.partials.${rowIndex}.signableFrom`, $ev)"
            />
          </template>
        </InputComponent>
      </template>
      <template #expires="{ item, rowIndex }">
        <InputComponent data-field="expiresAt">
          <template #default>
            <InputDate
              :value="formatDate(item.expiresAt)"
              @input="$ev => setDate(`value.data.partials.${rowIndex}.expiresAt`, $ev)"
            />
          </template>
        </InputComponent>
      </template>
    </DataTable>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import { DateTransformer, TimeTransformer } from '@/utils/transformers'
import { setByPath } from '@/utils'

import { InputComponent } from 'vue-elder-input'
import InputDate from '@/components/InputDate'
import DeliveryLog from '@/components/DeliveryLog'
import DataTable from '@/components/DataTable'

export default {
  props: {
    value: Object,
    labels: Object,
    validator: Object,
  },
  watch: {
    value: {
      handler: 'prepareData',
      immediate: true,
    },
  },
  computed: {
    ...mapState('Shop', {
      item: state => state.item,
      contract: state => state.item.contract || {},
    }),
    categories() {
      return this.item.categories
    },
    expiresAtDate: DateTransformer('value.expiresAt'),
    expiresAtTime: TimeTransformer('value.expiresAt'),
    canRemovePartials() {
      return this.value.data.partials && this.categories.every(({ status }) => !['signed', 'accepted'].includes(status))
    },
  },
  methods: {
    formatDate(val) {
      if (!val) return
      return moment(val).format('YYYY-MM-DD')
    },
    getPartialStatus(id) {
      let category = this.item.categories.find(c => c.id === id)
      if (!category) return 'draft'

      return category.status
    },
    getCategoryLabel(id) {
      return this.categories.find(c => c.id === id).label
    },
    getDeliveryLog(pattern) {
      let log = (this.contract.deliveryLogs || []).find(l => new RegExp(pattern, 'i').test(l.tag))

      return (
        log ||
        (this.contract.expiresAt || this.contract.data.signableFrom
          ? { events: [{ event: 'processing', date: new Date() }] }
          : undefined)
      )
    },
    prepareData() {
      if (!this.value.data) this.$set(this.value, 'data', {})
      if (!this.value.data.partials) this.$set(this.value.data, 'partials', null)
      if (!this.value.data.signableFrom) this.$set(this.value.data, 'signableFrom', null)
      if (this.value.data.partials) this.setPartials()
    },
    setPartials() {
      this.value.data = {
        ...this.value.data,
        partials: this.categories.map(cat => {
          let match = (this.$path('value.data.partials', this) || []).find(e => e.reference === cat.id)

          return {
            reference: cat.id,
            signableFrom: null,
            expiresAt: null,
            ...match,
          }
        }),
      }
    },
    unsetPartials() {
      this.value.data.partials = null
    },
    setDate(path, value) {
      if (value) {
        switch (path.split('.').pop()) {
          case 'signableFrom':
            value = moment(value).startOf('day').toDate()
            break
          case 'expiresAt':
            value = moment(value).endOf('day').toDate()
            break
          default:
            value = moment(value).toDate()
            break
        }
      }
      setByPath(path, value || null, this)
    },
  },
  components: {
    InputComponent,
    InputDate,
    DeliveryLog,
    DataTable,
  },
}
</script>

<style lang="scss">
.shop-expiry {
  &__partial {
    &--disabled {
      pointer-events: none;
      background-color: var(--light-grey);

      .input-date__reset {
        display: none;
      }
    }

    &-category {
      padding: 1em !important;
    }
  }

  &__row {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }

  .data-table {
    margin-top: 1rem;
    width: 100%;

    &__cell {
      padding: 0;
      font-size: var(--font-size-small);
      border-color: var(--border-color) !important;
    }

    &__row {
      &:not(:first-child) {
        .data-table__cell {
          border-right: 1px solid;

          &:first-child {
            border-left: 1px solid;
          }
        }
      }

      &:last-child .data-table__cell {
        border-bottom: 1px solid;
      }

      &-wrapper {
        .data-table__cell:first-child {
          padding: 0.5rem;
        }
      }
    }

    .elder-input__field {
      border: none;
      background-color: transparent;
    }
  }

  &__extend {
    text-decoration: none;
    margin-left: 5px;
    display: block;
    font-size: 0.8em;

    svg {
      margin-right: 5px;
    }
  }
}
</style>
